import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Package, 
  Calendar, 
  Users, 
  TrendingUp,
  Filter,
  Download,
  ChevronDown,
  ChevronUp,
  FileSpreadsheet,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const formatNumber = (num) => {
  return new Intl.NumberFormat('en-NG').format(num || 0);
};

const OrderPickupSummary = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const isDistributor = user?.role === 'distributor';
  
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [selectedDistributor, setSelectedDistributor] = useState('all');
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [exporting, setExporting] = useState(false);

  const years = [];
  for (let y = currentDate.getFullYear() - 2; y <= currentDate.getFullYear() + 1; y++) {
    years.push(y);
  }

  useEffect(() => {
    fetchSummary();
  }, [selectedYear, selectedMonth, selectedCustomer, selectedDistributor]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      let url = `${API}/order-pickup-summary?year=${selectedYear}&month=${selectedMonth}`;
      if (selectedCustomer !== 'all') {
        url += `&customer_id=${selectedCustomer}`;
      }
      if (selectedDistributor !== 'all' && isAdmin) {
        url += `&distributor_id=${selectedDistributor}`;
      }
      
      const response = await axios.get(url, getAuthHeader());
      setSummaryData(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
      toast.error('Failed to load pickup summary');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      let url = `${API}/order-pickup-summary/export?year=${selectedYear}&month=${selectedMonth}&format=${format}`;
      if (selectedCustomer !== 'all') {
        url += `&customer_id=${selectedCustomer}`;
      }
      if (selectedDistributor !== 'all' && isAdmin) {
        url += `&distributor_id=${selectedDistributor}`;
      }
      
      const response = await axios.get(url, {
        ...getAuthHeader(),
        responseType: format === 'csv' ? 'blob' : 'text'
      });
      
      if (format === 'csv') {
        // Download CSV file
        const blob = new Blob([response.data], { type: 'text/csv' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `pickup_summary_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
        toast.success('CSV downloaded successfully');
      } else {
        // Open PDF (HTML) in new tab for printing
        const printWindow = window.open('', '_blank');
        printWindow.document.write(response.data);
        printWindow.document.close();
        toast.success('PDF opened for printing');
      }
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  // Group monthly data by distributor
  const groupedByDistributor = () => {
    if (!summaryData?.monthly) return {};
    
    const groups = {};
    for (const customer of summaryData.monthly) {
      const distName = customer.affiliated_distributor_name || 'No Distributor';
      const distId = customer.affiliated_distributor_id || 'none';
      
      if (!groups[distId]) {
        groups[distId] = {
          name: distName,
          customers: [],
          totals: {
            extra_large_crates: 0, extra_large_pieces: 0,
            large_crates: 0, large_pieces: 0,
            medium_crates: 0, medium_pieces: 0,
            small_crates: 0, small_pieces: 0,
            cracks_crates: 0, cracks_pieces: 0,
            damaged_crates: 0, damaged_pieces: 0,
          }
        };
      }
      
      groups[distId].customers.push(customer);
      
      // Sum totals
      groups[distId].totals.extra_large_crates += customer.extra_large_crates || 0;
      groups[distId].totals.extra_large_pieces += customer.extra_large_pieces || 0;
      groups[distId].totals.large_crates += customer.large_crates || 0;
      groups[distId].totals.large_pieces += customer.large_pieces || 0;
      groups[distId].totals.medium_crates += customer.medium_crates || 0;
      groups[distId].totals.medium_pieces += customer.medium_pieces || 0;
      groups[distId].totals.small_crates += customer.small_crates || 0;
      groups[distId].totals.small_pieces += customer.small_pieces || 0;
      groups[distId].totals.cracks_crates += customer.cracks_crates || 0;
      groups[distId].totals.cracks_pieces += customer.cracks_pieces || 0;
      groups[distId].totals.damaged_crates += customer.damaged_crates || 0;
      groups[distId].totals.damaged_pieces += customer.damaged_pieces || 0;
    }
    
    return groups;
  };

  // Calculate monthly totals
  const monthlyTotals = () => {
    if (!summaryData?.monthly) return null;
    
    const totals = {
      extra_large_crates: 0, extra_large_pieces: 0,
      large_crates: 0, large_pieces: 0,
      medium_crates: 0, medium_pieces: 0,
      small_crates: 0, small_pieces: 0,
      cracks_crates: 0, cracks_pieces: 0,
      damaged_crates: 0, damaged_pieces: 0,
    };
    
    for (const customer of summaryData.monthly) {
      totals.extra_large_crates += customer.extra_large_crates || 0;
      totals.extra_large_pieces += customer.extra_large_pieces || 0;
      totals.large_crates += customer.large_crates || 0;
      totals.large_pieces += customer.large_pieces || 0;
      totals.medium_crates += customer.medium_crates || 0;
      totals.medium_pieces += customer.medium_pieces || 0;
      totals.small_crates += customer.small_crates || 0;
      totals.small_pieces += customer.small_pieces || 0;
      totals.cracks_crates += customer.cracks_crates || 0;
      totals.cracks_pieces += customer.cracks_pieces || 0;
      totals.damaged_crates += customer.damaged_crates || 0;
      totals.damaged_pieces += customer.damaged_pieces || 0;
    }
    
    return totals;
  };

  const grouped = groupedByDistributor();
  const totals = monthlyTotals();

  return (
    <div className="space-y-6" data-testid="order-pickup-summary-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Order Pick-up Summary
          </h1>
          <p className="text-slate-600 mt-1">
            {isDistributor ? 'Summary of affiliated customer pickups' : 'Summary of all customer pickups by egg size'}
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              className="gap-2"
              disabled={exporting || !summaryData?.monthly?.length}
              data-testid="export-summary-btn"
            >
              {exporting ? (
                <div className="animate-spin w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Export Summary
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleExport('csv')} className="gap-2 cursor-pointer">
              <FileSpreadsheet className="w-4 h-4 text-green-600" />
              Export as CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('pdf')} className="gap-2 cursor-pointer">
              <FileText className="w-4 h-4 text-red-600" />
              Export as PDF
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-slate-600" />
          <h3 className="font-semibold text-slate-800">Filters</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-sm text-slate-600 mb-1 block">Year</Label>
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger data-testid="year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm text-slate-600 mb-1 block">Month</Label>
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger data-testid="month-select">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map(m => (
                  <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isAdmin && summaryData?.distributors?.length > 0 && (
            <div>
              <Label className="text-sm text-slate-600 mb-1 block">Distributor</Label>
              <Select value={selectedDistributor} onValueChange={setSelectedDistributor}>
                <SelectTrigger data-testid="distributor-select">
                  <Users className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Distributors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Distributors</SelectItem>
                  {summaryData.distributors.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-sm text-slate-600 mb-1 block">Customer</Label>
            <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
              <SelectTrigger data-testid="customer-select">
                <SelectValue placeholder="All Customers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Customers</SelectItem>
                {summaryData?.customers?.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* YTD Summary Cards */}
      {summaryData?.ytd && (
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            <h3 className="font-semibold text-emerald-900">Year-to-Date (YTD) Totals - {selectedYear}</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div className="bg-white/70 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-purple-700">{formatNumber(summaryData.ytd.extra_large_crates)}</div>
              <div className="text-xs text-slate-600">X-Large Crates</div>
              <div className="text-xs text-slate-400">+{formatNumber(summaryData.ytd.extra_large_pieces)} pcs</div>
            </div>
            <div className="bg-white/70 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-blue-700">{formatNumber(summaryData.ytd.large_crates)}</div>
              <div className="text-xs text-slate-600">Large Crates</div>
              <div className="text-xs text-slate-400">+{formatNumber(summaryData.ytd.large_pieces)} pcs</div>
            </div>
            <div className="bg-white/70 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-green-700">{formatNumber(summaryData.ytd.medium_crates)}</div>
              <div className="text-xs text-slate-600">Medium Crates</div>
              <div className="text-xs text-slate-400">+{formatNumber(summaryData.ytd.medium_pieces)} pcs</div>
            </div>
            <div className="bg-white/70 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-amber-700">{formatNumber(summaryData.ytd.small_crates)}</div>
              <div className="text-xs text-slate-600">Small Crates</div>
              <div className="text-xs text-slate-400">+{formatNumber(summaryData.ytd.small_pieces)} pcs</div>
            </div>
            <div className="bg-white/70 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-orange-600">{formatNumber(summaryData.ytd.cracks_crates)}</div>
              <div className="text-xs text-slate-600">Cracks Crates</div>
              <div className="text-xs text-slate-400">+{formatNumber(summaryData.ytd.cracks_pieces)} pcs</div>
            </div>
            <div className="bg-white/70 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-red-600">{formatNumber(summaryData.ytd.damaged_crates)}</div>
              <div className="text-xs text-slate-600">Damaged Crates</div>
              <div className="text-xs text-slate-400">+{formatNumber(summaryData.ytd.damaged_pieces)} pcs</div>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Summary Cards */}
      {totals && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-4">
            <Package className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-blue-900">
              Monthly Totals - {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
            </h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div className="bg-white/70 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-purple-700">{formatNumber(totals.extra_large_crates)}</div>
              <div className="text-xs text-slate-600">X-Large Crates</div>
              <div className="text-xs text-slate-400">+{formatNumber(totals.extra_large_pieces)} pcs</div>
            </div>
            <div className="bg-white/70 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-blue-700">{formatNumber(totals.large_crates)}</div>
              <div className="text-xs text-slate-600">Large Crates</div>
              <div className="text-xs text-slate-400">+{formatNumber(totals.large_pieces)} pcs</div>
            </div>
            <div className="bg-white/70 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-green-700">{formatNumber(totals.medium_crates)}</div>
              <div className="text-xs text-slate-600">Medium Crates</div>
              <div className="text-xs text-slate-400">+{formatNumber(totals.medium_pieces)} pcs</div>
            </div>
            <div className="bg-white/70 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-amber-700">{formatNumber(totals.small_crates)}</div>
              <div className="text-xs text-slate-600">Small Crates</div>
              <div className="text-xs text-slate-400">+{formatNumber(totals.small_pieces)} pcs</div>
            </div>
            <div className="bg-white/70 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-orange-600">{formatNumber(totals.cracks_crates)}</div>
              <div className="text-xs text-slate-600">Cracks Crates</div>
              <div className="text-xs text-slate-400">+{formatNumber(totals.cracks_pieces)} pcs</div>
            </div>
            <div className="bg-white/70 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-red-600">{formatNumber(totals.damaged_crates)}</div>
              <div className="text-xs text-slate-600">Damaged Crates</div>
              <div className="text-xs text-slate-400">+{formatNumber(totals.damaged_pieces)} pcs</div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
          <span className="ml-3 text-slate-600">Loading summary data...</span>
        </div>
      ) : (
        /* Grouped Data by Distributor */
        <div className="space-y-4">
          {Object.entries(grouped).map(([distId, group]) => (
            <div key={distId} className="bg-white rounded-xl border shadow-sm overflow-hidden">
              {/* Distributor Header */}
              <button
                onClick={() => toggleGroup(distId)}
                className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-slate-600" />
                  <div className="text-left">
                    <h3 className="font-semibold text-slate-800">{group.name}</h3>
                    <p className="text-sm text-slate-500">{group.customers.length} customer(s)</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right text-sm">
                    <span className="text-slate-600">Total: </span>
                    <span className="font-semibold text-blue-600">
                      {formatNumber(group.totals.large_crates + group.totals.medium_crates + group.totals.small_crates + group.totals.extra_large_crates)} crates
                    </span>
                  </div>
                  {expandedGroups[distId] ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </button>
              
              {/* Distributor Totals Row */}
              <div className="grid grid-cols-6 gap-2 p-3 bg-blue-50 text-sm font-medium text-slate-700 border-b">
                <div className="text-center">
                  <div className="text-purple-600">{formatNumber(group.totals.extra_large_crates)}</div>
                  <div className="text-xs text-slate-500">X-Large</div>
                </div>
                <div className="text-center">
                  <div className="text-blue-600">{formatNumber(group.totals.large_crates)}</div>
                  <div className="text-xs text-slate-500">Large</div>
                </div>
                <div className="text-center">
                  <div className="text-green-600">{formatNumber(group.totals.medium_crates)}</div>
                  <div className="text-xs text-slate-500">Medium</div>
                </div>
                <div className="text-center">
                  <div className="text-amber-600">{formatNumber(group.totals.small_crates)}</div>
                  <div className="text-xs text-slate-500">Small</div>
                </div>
                <div className="text-center">
                  <div className="text-orange-600">{formatNumber(group.totals.cracks_crates)}</div>
                  <div className="text-xs text-slate-500">Cracks</div>
                </div>
                <div className="text-center">
                  <div className="text-red-600">{formatNumber(group.totals.damaged_crates)}</div>
                  <div className="text-xs text-slate-500">Damaged</div>
                </div>
              </div>
              
              {/* Customer Details (Expandable) */}
              {expandedGroups[distId] && (
                <div className="divide-y">
                  {group.customers.map(customer => (
                    <div key={customer.customer_id} className="p-4 hover:bg-slate-50">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-slate-800">{customer.customer_name}</h4>
                      </div>
                      <div className="grid grid-cols-6 gap-2 text-sm">
                        <div className="text-center">
                          <div className="font-semibold text-purple-600">{formatNumber(customer.extra_large_crates)}</div>
                          <div className="text-xs text-slate-400">+{customer.extra_large_pieces}pcs</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-blue-600">{formatNumber(customer.large_crates)}</div>
                          <div className="text-xs text-slate-400">+{customer.large_pieces}pcs</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-green-600">{formatNumber(customer.medium_crates)}</div>
                          <div className="text-xs text-slate-400">+{customer.medium_pieces}pcs</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-amber-600">{formatNumber(customer.small_crates)}</div>
                          <div className="text-xs text-slate-400">+{customer.small_pieces}pcs</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-orange-600">{formatNumber(customer.cracks_crates)}</div>
                          <div className="text-xs text-slate-400">+{customer.cracks_pieces}pcs</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold text-red-600">{formatNumber(customer.damaged_crates)}</div>
                          <div className="text-xs text-slate-400">+{customer.damaged_pieces}pcs</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          
          {Object.keys(grouped).length === 0 && !loading && (
            <div className="bg-white rounded-xl border p-12 text-center">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600">No pickup data found</h3>
              <p className="text-slate-400 mt-1">No pickups recorded for the selected period</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderPickupSummary;
